// Utility to help with development authentication using real SSO tokens
export class DevAuthHelper {

  /**
   * Get SSO token from cookies (for development use)
   * Note: This only works for same-origin cookies due to CORS
   */
  static getSSOTokenFromCookie(): string | null {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'sso-token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  /**
   * Attempt to get SSO token via iframe (limited by CORS)
   */
  static async tryGetTokenViaIframe(): Promise<string | null> {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = 'https://control.itsupport247.net/';

      const timeout = setTimeout(() => {
        document.body.removeChild(iframe);
        resolve(null);
      }, 5000);

      iframe.onload = () => {
        try {
          // This will fail due to CORS, but we try anyway
          const iframeCookies = iframe.contentDocument?.cookie;
          if (iframeCookies) {
            const cookies = iframeCookies.split(';');
            for (let cookie of cookies) {
              const [name, value] = cookie.trim().split('=');
              if (name === 'sso-token') {
                clearTimeout(timeout);
                document.body.removeChild(iframe);
                resolve(decodeURIComponent(value));
                return;
              }
            }
          }
        } catch {
          console.log('CORS prevented iframe cookie access (expected)');
        }

        clearTimeout(timeout);
        document.body.removeChild(iframe);
        resolve(null);
      };

      document.body.appendChild(iframe);
    });
  }

  /**
   * Instructions for getting SSO token manually
   */
  static getInstructions(): string {
    return `
🔧 Development SSO Token Setup:

1. Open a new tab and go to: https://control.itsupport247.net/
2. Log in with your ConnectWise credentials
3. Open Developer Tools (F12)
4. Go to Application tab > Cookies > https://control.itsupport247.net
5. Find the "sso-token" cookie and copy its value
6. Come back to this tab and paste it in the dev tools console:

   localStorage.setItem('dev-sso-token', 'PASTE_TOKEN_HERE');

7. Refresh this page

OR

Use the "Import SSO Token" button below to paste it directly.
    `;
  }

  /**
   * Store SSO token for development use (both localStorage and cookie)
   */
  static storeSSOToken(token: string): void {
    if (token && token.trim()) {
      const cleanToken = token.trim();

      // Store in localStorage for immediate use
      localStorage.setItem('dev-sso-token', cleanToken);

      // Store as a cookie for persistence across sessions
      // Set for 7 days with localhost domain
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7);

      document.cookie = `dev-sso-token=${encodeURIComponent(cleanToken)}; expires=${expirationDate.toUTCString()}; path=/; domain=localhost; SameSite=Lax`;

      console.log('✅ SSO Token stored in localStorage and cookie (expires in 7 days)');
    }
  }

  /**
   * Get stored SSO token (try cookie first, then localStorage)
   */
  static getStoredSSOToken(): string | null {
    // First try to get from cookie (persists across sessions)
    const cookieToken = this.getCookieValue('dev-sso-token');
    if (cookieToken) {
      // Sync to localStorage if not there
      if (!localStorage.getItem('dev-sso-token')) {
        localStorage.setItem('dev-sso-token', cookieToken);
      }
      return cookieToken;
    }

    // Fallback to localStorage
    const localToken = localStorage.getItem('dev-sso-token');
    if (localToken) {
      // Sync to cookie if not there
      this.storeSSOToken(localToken);
      return localToken;
    }

    return null;
  }

  /**
   * Get cookie value by name
   */
  private static getCookieValue(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift();
      return cookieValue || null;
    }
    return null;
  }

  /**
   * Clear stored SSO token from both localStorage and cookie
   */
  static clearSSOToken(): void {
    localStorage.removeItem('dev-sso-token');

    // Clear cookie by setting it to expire in the past
    document.cookie = 'dev-sso-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost; SameSite=Lax';

    console.log('Dev SSO token cleared from localStorage and cookie');
  }

  /**
   * Check if we have a valid SSO token
   */
  static hasValidToken(): boolean {
    const token = this.getStoredSSOToken();
    return token !== null && token.length > 10; // Basic validation
  }

  /**
   * Create mock user with real SSO token
   */
  static createMockUserWithRealToken(): any {
    const token = this.getStoredSSOToken();
    if (!token) return null;

    return {
      access_token: token,
      id_token: 'dev_id_token',
      token_type: 'Bearer',
      profile: {
        name: 'Development User (Real Token)',
        email: 'dev@connectwise.com',
        sub: 'dev_user_real_token'
      },
      expired: false
    };
  }
}

export default DevAuthHelper;
